import { useMutation } from '@apollo/client';
import { useVirtualizer } from '@tanstack/react-virtual';
import findLastIndex from 'lodash/findLastIndex';
import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { match } from 'ts-pattern';

import { AudioPlayerHandle } from '@eluve/audio-player';
import { TranscriptText, TranscriptTimestampDivider } from '@eluve/blocks';
import {
  Button,
  DebugJson,
  Dialog,
  DialogContent,
  DialogTrigger,
  FCC,
  Stack,
  Text,
} from '@eluve/components';
import { getReadableTime } from '@eluve/date-utils';
import { FragmentOf } from '@eluve/graphql.tada';
import { useIsEluveAdmin, useUserIdFromSession } from '@eluve/session-helpers';
import { EluveAdminOnly } from '@eluve/smart-blocks';
import { formatTranscriptForDisplay } from '@eluve/utils';

import { FocusableTranscript } from './FocusableTranscript';
import { MemoizedUtteranceCheckbox } from './MemoizedUtteranceCheckbox';
import {
  SegmentAudioContext,
  SegmentAudioContextValue,
  createSegmentAudioContextStore,
  useSegmentAudioContext,
} from './SegmentAudioContextStore';
import { SegmentAudioControls } from './SegmentAudioControls';
import SegmentKaraokeLogic from './SegmentKaraokeLogic';
import { SegmentSpeakerControls } from './SegmentSpeakerControls';
import { UtteranceActions } from './UtteranceActions';
import { MemoizedUtteranceTimestamp } from './UtteranceTimestamp';
import { segmentFragment, updateUtteranceTextMutation } from './operations';
import { useSegmentAudioData } from './useTranscriptAudioAnalysisData';
import { useUtteranceOperations } from './useUtteranceOperations';

type SegmentAudioContextProviderProps = {
  index: number;
  tenantId: string;
  appointmentId: string;
  segmentId: string;
  seg: FragmentOf<typeof segmentFragment>;
  isReadonly?: boolean;
};

export const SegmentAudioContextProvider: FCC<
  SegmentAudioContextProviderProps
> = (props) => {
  const { tenantId, segmentId, isReadonly = false, seg: segment } = props;
  const audioPlayerRef = useRef<AudioPlayerHandle>(null);

  const store = useMemo(() => {
    return createSegmentAudioContextStore({
      tenantId,
      segmentId,
      isReadonly,
    });
  }, [tenantId, segmentId, isReadonly]);

  const value: SegmentAudioContextValue = useMemo(
    () => ({
      store,
      audioPlayerHandle: audioPlayerRef,
    }),
    [store],
  );

  useEffect(() => {
    store.send({ type: 'updateSegment', segment });
  }, [store, segment]);

  return (
    <SegmentAudioContext.Provider value={value}>
      <SegmentAudio {...props} />
    </SegmentAudioContext.Provider>
  );
};

const SegmentAudio: FCC<SegmentAudioContextProviderProps> = ({
  appointmentId,
  seg,
  tenantId,
  index,
  isReadonly = false,
}) => {
  const isEluveAdmin = useIsEluveAdmin();
  const userId = useUserIdFromSession();
  const showConfidenceStyles = true;

  const [updateUtterance] = useMutation(updateUtteranceTextMutation);
  const onUtteranceChanged = (id: string, newUtteranceText: string) => {
    if (isReadonly) return;
    updateUtterance({
      variables: {
        tenantId,
        id,
        text: newUtteranceText,
      },
      optimisticResponse: () => ({
        updateUtterancesByPk: {
          __typename: 'Utterances',
          id,
          text: newUtteranceText,
          updatedById: userId,
          hasHumanUpdatedText: true,
        },
      }),
    });
  };

  const { resetUtterance, mergeUtterance, splitUtterance } =
    useUtteranceOperations({
      appointmentId,
      segmentId: seg.id,
      tenantId,
    });

  const { isLoading, result } = useSegmentAudioData({
    appointmentId,
    seg,
    tenantId,
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: seg.utterances.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 6,
  });

  const scrollToLatestEdit = useCallback(() => {
    const lastUtteranceIndex = findLastIndex(
      seg.utterances,
      (u) => u.hasHumanUpdatedText,
    );
    if (lastUtteranceIndex !== -1) {
      virtualizer.scrollToIndex(lastUtteranceIndex);
      setTimeout(() => {
        const allEdits = document.querySelectorAll('[data-edited-by]');
        if (allEdits.length === 0) return;
        const lastEdit = allEdits[allEdits.length - 1] as HTMLElement;
        lastEdit.classList.add('bg-bg-orange-strong', 'animate-pulse');
        setTimeout(() => {
          lastEdit.classList.remove('bg-bg-orange-strong', 'animate-pulse');
        }, 2200);
      }, 250);
    }
  }, [seg.utterances, virtualizer]);

  const items = virtualizer.getVirtualItems();
  const { audioPlayerHandle } = useSegmentAudioContext();

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Space' && event.shiftKey) {
        event.preventDefault();
        if (audioPlayerHandle.current) {
          if (audioPlayerHandle.current.isPaused()) {
            audioPlayerHandle.current.play();
          } else {
            audioPlayerHandle.current.pause();
          }
        }
      }
    },
    [audioPlayerHandle],
  );

  useEffect(() => {
    const ignoreKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space' && event.shiftKey) {
        event.preventDefault();
      }
    };
    document.addEventListener('keydown', ignoreKeyDown);
    document.addEventListener('keyup', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', ignoreKeyDown);
      document.removeEventListener('keyup', handleKeyPress);
    };
  }, [handleKeyPress]);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (result.type === 'no_audio') {
    return <div>No audio available</div>;
  }

  const ctx = result.context;

  function shouldDisplaySplitter(
    nextStartAt: number | null | undefined,
    endAt: number | null | undefined,
  ): boolean {
    if (nextStartAt && endAt) {
      return (nextStartAt - endAt) * 1000 > 1000; // if there is more than XXms of unaccount for audio allow splitting
    }
    return false;
  }

  return (
    <>
      <Stack className="sticky top-0 z-10 bg-white" gap={2}>
        <TranscriptTimestampDivider
          timestamp={ctx.startedAt}
          action={index === 0 ? 'started' : 'resumed'}
        />
        <Stack gap={2} direction="horizontal" wrap>
          {ctx.audioUrl && <SegmentAudioControls audioUrl={ctx.audioUrl} />}

          {isEluveAdmin &&
            (seg.raw_transcript
              .filter((r) => r.transcriptionStatus === 'COMPLETED')
              .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
              ?.transcriptionSource ??
              'Unknown Model')}
          {seg.utterances.length > 0 && (
            <SegmentKaraokeLogic onGoToLatestEdit={scrollToLatestEdit} />
          )}
        </Stack>
        <EluveAdminOnly>
          {!isEmpty(ctx.rawMetadata) && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="secondary">Raw Metadata</Button>
              </DialogTrigger>
              <DialogContent size="full">
                <Stack className="max-h-[600px] overflow-auto" gap={2}>
                  <DebugJson value={ctx.rawMetadata} />
                </Stack>
              </DialogContent>
            </Dialog>
          )}
        </EluveAdminOnly>
      </Stack>

      {match(seg.utterances.length)
        .with(0, () => (
          <TranscriptText>
            {formatTranscriptForDisplay(seg.transcript ?? '')}
          </TranscriptText>
        ))
        .otherwise(() => {
          const initialSpeakerNames = new Set(
            seg.utterances.map((u) => u.speaker).filter(Boolean) as string[],
          );

          return (
            <>
              <SegmentSpeakerControls
                tenantId={tenantId}
                segmentId={seg.id}
                initialSpeakerNames={[...initialSpeakerNames]}
              />
              <div
                data-karaoke-segment-id={seg.id}
                ref={parentRef}
                style={{
                  height: 600,
                  width: '100%',
                  overflowY: 'auto',
                  contain: 'strict',
                }}
              >
                <Stack
                  className="gap-x-5 pt-1 pr-1"
                  style={{
                    height: virtualizer.getTotalSize(),
                  }}
                  align="stretch"
                >
                  {items.map((virtualRow) => {
                    const uIdx = virtualRow.index;
                    const u = seg.utterances[uIdx]!;
                    const segDurationOverOneHour =
                      (seg.recordingDuration ?? 0) > 3600 * 1000;
                    const nextUtterance = seg.utterances[uIdx + 1];
                    const showSpeaker =
                      u.speaker &&
                      (uIdx === 0 ||
                        u.speaker !== seg.utterances[uIdx - 1]?.speaker);
                    return (
                      <Stack
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={virtualizer.measureElement}
                        className="absolute top-0 left-0"
                        style={{
                          transform: `translateY(${
                            virtualRow.start - virtualizer.options.scrollMargin
                          }px)`,
                        }}
                        gap={2}
                        direction="horizontal"
                      >
                        <MemoizedUtteranceCheckbox utteranceId={u.id} />
                        <Text
                          inline
                          className="flex w-24 items-center justify-center whitespace-nowrap underline"
                        >
                          {showSpeaker ? u.speaker : null}
                        </Text>
                        <MemoizedUtteranceTimestamp
                          utterance={u}
                          nextUtteranceStartAt={nextUtterance?.startAt ?? null}
                        >
                          {u.startAt &&
                            getReadableTime(
                              u.startAt,
                              Boolean(segDurationOverOneHour),
                            )}
                        </MemoizedUtteranceTimestamp>
                        <Stack
                          gap={1}
                          fullWidth={false}
                          className="py-0.5"
                          direction="horizontal"
                        >
                          <UtteranceActions
                            utteranceIdx={uIdx}
                            utteranceId={u.id}
                            isReadonly={isReadonly}
                            hasHumanUpdatedText={u.hasHumanUpdatedText}
                            mergeUtterance={mergeUtterance}
                            resetUtterance={resetUtterance}
                          />
                        </Stack>
                        <Stack
                          gap={1}
                          wrap
                          className="group/line min-h-4"
                          direction="horizontal"
                          align="center"
                          justify="start"
                        >
                          <FocusableTranscript
                            isReadonly={isReadonly}
                            utterance={u}
                            nextUtterance={nextUtterance}
                            showConfidenceStyles={showConfidenceStyles}
                            onUtteranceChanged={onUtteranceChanged}
                            splitUtterance={splitUtterance}
                            shouldDisplaySplitter={shouldDisplaySplitter}
                          />
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
                {ctx.pausedAt && (
                  <TranscriptTimestampDivider
                    className="col-span-5"
                    timestamp={ctx.pausedAt}
                    action="paused"
                  />
                )}
              </div>
            </>
          );
        })}
    </>
  );
};
